import React, { useState } from 'react';
import { Maximize2, Check, SlidersHorizontal, Image as ImageIcon, Globe } from 'lucide-react';
import type { ResizeOptions, ResizeMode } from '../types';
import {
  PORTAL_PRESETS,
  MARKETING_PRESETS,
  TOUR_360_PRESETS,
  ALL_PRESETS,
  DEFAULT_TOUR_360_WIDTH,
  DEFAULT_TOUR_360_QUALITY,
  DEFAULT_STANDARD_QUALITY,
} from '../constants';
import type { ResizePreset } from '../constants';

interface ResizeControlsProps {
  options: ResizeOptions;
  onChange: (options: ResizeOptions) => void;
}

const PresetCard: React.FC<{
  preset: ResizePreset;
  selected: boolean;
  onClick: () => void;
}> = ({ preset, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`relative flex flex-col items-start p-3.5 rounded-xl border text-left transition-all ${
      selected
        ? 'bg-cyan-600/15 border-cyan-500/50 ring-1 ring-cyan-500/30'
        : 'bg-slate-800/60 border-slate-700 hover:border-cyan-500/30 hover:bg-slate-800'
    }`}
  >
    {selected && (
      <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center">
        <Check size={12} className="text-white" strokeWidth={3} />
      </div>
    )}
    <span className={`text-sm font-semibold ${selected ? 'text-cyan-200' : 'text-slate-100'}`}>
      {preset.name}
    </span>
    <span className={`text-xs font-medium mt-0.5 ${selected ? 'text-cyan-400' : 'text-slate-400'}`}>
      {preset.width}px
    </span>
    <span className="text-[11px] text-slate-500 mt-1.5 leading-snug">
      {preset.description}
    </span>
  </button>
);

const ModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  sub: string;
}> = ({ active, onClick, icon, label, sub }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
      active
        ? 'bg-cyan-600/15 border-cyan-500/50 ring-1 ring-cyan-500/30'
        : 'bg-slate-800/60 border-slate-700 hover:border-cyan-500/30'
    }`}
  >
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
      active ? 'bg-cyan-500/20 text-cyan-300' : 'bg-slate-700/50 text-slate-400'
    }`}>
      {icon}
    </div>
    <div className="min-w-0">
      <div className={`text-sm font-semibold ${active ? 'text-cyan-200' : 'text-slate-200'}`}>{label}</div>
      <div className="text-[11px] text-slate-500 leading-tight">{sub}</div>
    </div>
  </button>
);

const ResizeControls: React.FC<ResizeControlsProps> = ({ options, onChange }) => {
  const [customWidth, setCustomWidth] = useState('');
  const isTour360 = options.mode === 'tour360';
  const isPreset = options.targetWidth !== null && ALL_PRESETS.some((p) => p.width === options.targetWidth);
  const isCustomActive = options.targetWidth !== null && !isPreset;
  const isOriginal = options.targetWidth === null;

  const selectPreset = (preset: ResizePreset) => {
    onChange({ ...options, targetWidth: preset.width });
    setCustomWidth('');
  };

  const setMode = (mode: ResizeMode) => {
    if (mode === options.mode) return;
    if (mode === 'tour360') {
      onChange({
        mode: 'tour360',
        targetWidth: DEFAULT_TOUR_360_WIDTH,
        quality: options.quality ?? DEFAULT_TOUR_360_QUALITY,
      });
    } else {
      onChange({
        mode: 'standard',
        targetWidth: 1600,
        quality: DEFAULT_STANDARD_QUALITY,
      });
    }
    setCustomWidth('');
  };

  const quality = options.quality ?? (isTour360 ? DEFAULT_TOUR_360_QUALITY : DEFAULT_STANDARD_QUALITY);
  const qualityPct = Math.round(quality * 100);

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-8 h-8 rounded-lg bg-cyan-900/30 border border-cyan-500/20 flex items-center justify-center">
          <Maximize2 size={16} className="text-cyan-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-200">Resize & Optimise</h3>
        <span className="text-xs text-slate-500 ml-auto">
          {isTour360 ? '2:1 ratio enforced' : 'Aspect ratio preserved'}
        </span>
      </div>

      {/* Mode Selector */}
      <div className="flex gap-2 mb-5">
        <ModeButton
          active={!isTour360}
          onClick={() => setMode('standard')}
          icon={<ImageIcon size={18} />}
          label="Standard photos"
          sub="Listings, marketing, social"
        />
        <ModeButton
          active={isTour360}
          onClick={() => setMode('tour360')}
          icon={<Globe size={18} />}
          label="360° panoramas"
          sub="Equirectangular for virtual tours"
        />
      </div>

      {isTour360 ? (
        <>
          {/* 360 Presets */}
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Panorama Width (height auto-locked to width ÷ 2)
            </h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {TOUR_360_PRESETS.map((preset) => (
                <PresetCard
                  key={preset.name}
                  preset={preset}
                  selected={options.targetWidth === preset.width}
                  onClick={() => selectPreset(preset)}
                />
              ))}
            </div>
          </div>

          {/* Custom Width (360) */}
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Custom Width</h4>
            <div
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                isCustomActive
                  ? 'bg-cyan-600/15 border-cyan-500/50 ring-1 ring-cyan-500/30'
                  : 'bg-slate-800/60 border-slate-700'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                isCustomActive ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-700/50 text-slate-500'
              }`}>
                <SlidersHorizontal size={16} />
              </div>
              <input
                type="number"
                min={512}
                max={16384}
                step={2}
                placeholder="e.g. 4096"
                value={customWidth}
                onChange={(e) => {
                  setCustomWidth(e.target.value);
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 512) {
                    onChange({ ...options, targetWidth: val });
                  } else if (e.target.value === '') {
                    onChange({ ...options, targetWidth: null });
                  }
                }}
                className="flex-1 px-3 py-2 bg-black/50 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
              />
              <span className="text-xs text-slate-500 shrink-0">px wide</span>
            </div>
          </div>

          {/* JPEG Quality slider — the main MB-reduction knob for panoramas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">JPEG Quality</h4>
              <span className="text-xs font-semibold text-cyan-400">{qualityPct}%</span>
            </div>
            <input
              type="range"
              min={40}
              max={95}
              step={1}
              value={qualityPct}
              onChange={(e) => onChange({ ...options, quality: parseInt(e.target.value, 10) / 100 })}
              className="w-full accent-cyan-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>Smaller file</span>
              <span>Higher quality</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 leading-snug">
              Output is forced to JPEG at the selected quality. 80% is a good default — typically halves the file size with no
              visible loss inside a 360 viewer.
            </p>
          </div>

          <div className="mt-4 p-3 rounded-lg bg-cyan-900/15 border border-cyan-500/20 text-[11px] text-cyan-200/90 leading-relaxed">
            Uploads must be 2:1 equirectangular panoramas. Anything that isn't (within 5% tolerance) will be skipped and
            listed after processing.
          </div>
        </>
      ) : (
        <>
          {/* Original Size Option */}
          <button
            onClick={() => { onChange({ ...options, targetWidth: null }); setCustomWidth(''); }}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border mb-5 transition-all ${
              isOriginal
                ? 'bg-cyan-600/15 border-cyan-500/50 ring-1 ring-cyan-500/30'
                : 'bg-slate-800/60 border-slate-700 hover:border-cyan-500/30'
            }`}
          >
            {isOriginal && (
              <div className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center shrink-0">
                <Check size={12} className="text-white" strokeWidth={3} />
              </div>
            )}
            <span className={`text-sm font-medium ${isOriginal ? 'text-cyan-200' : 'text-slate-300'}`}>
              Original size — no resizing
            </span>
          </button>

          {/* Portal Presets */}
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Property Portal Sizes</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {PORTAL_PRESETS.map((preset) => (
                <PresetCard
                  key={preset.name}
                  preset={preset}
                  selected={options.targetWidth === preset.width}
                  onClick={() => selectPreset(preset)}
                />
              ))}
            </div>
          </div>

          {/* Marketing Presets */}
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Marketing & Social Sizes</h4>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {MARKETING_PRESETS.map((preset) => (
                <PresetCard
                  key={preset.name}
                  preset={preset}
                  selected={options.targetWidth === preset.width}
                  onClick={() => selectPreset(preset)}
                />
              ))}
            </div>
          </div>

          {/* Custom Width */}
          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Custom Width</h4>
            <div
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                isCustomActive
                  ? 'bg-cyan-600/15 border-cyan-500/50 ring-1 ring-cyan-500/30'
                  : 'bg-slate-800/60 border-slate-700'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                isCustomActive ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-700/50 text-slate-500'
              }`}>
                <SlidersHorizontal size={16} />
              </div>
              <input
                type="number"
                min={100}
                max={10000}
                placeholder="Enter width (e.g. 1500px)"
                value={customWidth}
                onChange={(e) => {
                  setCustomWidth(e.target.value);
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 100) {
                    onChange({ ...options, targetWidth: val });
                  } else if (e.target.value === '') {
                    onChange({ ...options, targetWidth: null });
                  }
                }}
                className="flex-1 px-3 py-2 bg-black/50 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
              />
              <span className="text-xs text-slate-500 shrink-0">px</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-600 mt-4 text-center">
            Optimised sizes for property portals and marketing platforms.
          </p>
        </>
      )}
    </div>
  );
};

export default ResizeControls;
